import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { ModuleGuard } from '@/components/module-guard'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

const structureItemSchema = z.object({
  componentId: z.number().min(1, 'Component required'),
  calculationType: z.enum(['fixed', 'percent_of_base', 'percent_of_gross']),
  amount: z.string().min(1, 'Amount required'),
  sortOrder: z.number().optional(),
})

const createStructureSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  currency: z.string().optional(),
  items: z.array(structureItemSchema).optional(),
})

type CreateStructureFormValues = z.infer<typeof createStructureSchema>

type SalaryStructure = {
  id: number
  name: string
  description?: string | null
  currency?: string | null
  isActive?: boolean | null
  items?: unknown[]
}

type SalaryComponent = {
  id: number
  name: string
  code: string
  type: string
}

export const Route = createFileRoute('/hr/payroll/structures/')({
  component: SalaryStructuresPage,
})

function SalaryStructuresPage() {
  return (
    <ModuleGuard moduleKey="hr">
      <SalaryStructuresList />
    </ModuleGuard>
  )
}

function SalaryStructuresList() {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: number | null }>({
    open: false,
    id: null,
  })

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: structuresData, isLoading } = useQuery(
    orpc.hr.listSalaryStructures.queryOptions({ input: {} }),
  )

  const { data: componentsData } = useQuery(
    orpc.hr.listSalaryComponents.queryOptions({ input: {} }),
  )

  const structures = (structuresData ?? []) as SalaryStructure[]
  const components = (componentsData ?? []) as SalaryComponent[]

  const createMutation = useMutation(
    orpc.hr.createSalaryStructure.mutationOptions({
      onSuccess: () => {
        toast.success('Structure created')
        queryClient.invalidateQueries({ queryKey: [['hr', 'listSalaryStructures']] })
        setIsFormOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const deleteMutation = useMutation(
    orpc.hr.deleteSalaryStructure.mutationOptions({
      onSuccess: () => {
        toast.success('Structure deleted')
        queryClient.invalidateQueries({ queryKey: [['hr', 'listSalaryStructures']] })
        setDeleteDialog({ open: false, id: null })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const handleSubmit = async (data: CreateStructureFormValues) => {
    await createMutation.mutateAsync({
      ...data,
      items: (data.items ?? []).map((item) => ({
        componentId: item.componentId,
        calculationType: item.calculationType,
        amount: Number(item.amount),
        sortOrder: item.sortOrder,
      })),
    })
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Salary Structures</h1>
          <p className="text-muted-foreground">Define salary structure templates for employees</p>
        </div>
        <Button onClick={() => setIsFormOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Structure
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : structures.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No salary structures found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {structures.map((structure) => (
                  <TableRow
                    key={structure.id}
                    className="cursor-pointer"
                    onClick={() => void navigate({ to: `/hr/payroll/structures/${structure.id}` })}
                  >
                    <TableCell className="font-medium">{structure.name}</TableCell>
                    <TableCell>{structure.currency ?? 'USD'}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          structure.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }
                      >
                        {structure.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>{(structure.items ?? []).length}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600"
                        onClick={(e) => {
                          e.stopPropagation()
                          setDeleteDialog({ open: true, id: structure.id })
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateStructureDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        components={components}
        onSubmit={handleSubmit}
      />

      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, id: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Structure</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this salary structure? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
              onClick={() => deleteDialog.id && deleteMutation.mutate({ id: deleteDialog.id })}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface CreateStructureDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  components: SalaryComponent[]
  onSubmit: (data: CreateStructureFormValues) => Promise<void>
}

function CreateStructureDialog({ open, onOpenChange, components, onSubmit }: CreateStructureDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<CreateStructureFormValues>({
    resolver: zodResolver(createStructureSchema),
    defaultValues: {
      name: '',
      description: '',
      currency: 'USD',
      items: [],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'items',
  })

  const handleSubmit = async (data: CreateStructureFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Salary Structure</DialogTitle>
          <DialogDescription>Create a salary structure with components</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Standard Monthly" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency</FormLabel>
                    <FormControl>
                      <Input placeholder="USD" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Optional description..." rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div>
              <div className="flex items-center justify-between mb-3">
                <FormLabel>Components</FormLabel>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    append({ componentId: 0, calculationType: 'fixed', amount: '', sortOrder: fields.length + 1 })
                  }
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Item
                </Button>
              </div>

              {fields.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4 border rounded-md">
                  No components yet. Add items above.
                </p>
              )}

              {fields.map((field, index) => (
                <div key={field.id} className="flex gap-2 mb-2 items-end">
                  <FormField
                    control={form.control}
                    name={`items.${index}.componentId`}
                    render={({ field: f }) => (
                      <FormItem className="flex-1">
                        {index === 0 && <FormLabel>Component</FormLabel>}
                        <Select
                          onValueChange={(v) => f.onChange(Number(v))}
                          value={f.value ? f.value.toString() : ''}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select component" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {components.map((c) => (
                              <SelectItem key={c.id} value={c.id.toString()}>
                                {c.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name={`items.${index}.calculationType`}
                    render={({ field: f }) => (
                      <FormItem className="w-[130px]">
                        {index === 0 && <FormLabel>Calc Type</FormLabel>}
                        <Select
                          onValueChange={(v) => f.onChange(v as 'fixed' | 'percent_of_base' | 'percent_of_gross')}
                          value={f.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="fixed">Fixed</SelectItem>
                            <SelectItem value="percent_of_base">% of Base</SelectItem>
                            <SelectItem value="percent_of_gross">% of Gross</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name={`items.${index}.amount`}
                    render={({ field: f }) => (
                      <FormItem className="w-[100px]">
                        {index === 0 && <FormLabel>Amount</FormLabel>}
                        <FormControl>
                          <Input type="number" placeholder="0" {...f} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-red-600 mb-0.5"
                    onClick={() => remove(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Structure'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
